import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import PageHeader from '../../components/shell/PageHeader';
import ListScreen from '../../components/list/ListScreen';
import useListState from '../../components/list/useListState';
import Drawer, { DrawerField, DrawerSection } from '../../components/overlays/Drawer';
import Button from '../../components/ui/Button';
import { Field, TextInput, Textarea } from '../../components/ui/Field';
import { useOverlays } from '../../components/overlays/OverlayProvider';
import { getNameCorrectionCounts, listNameCorrections } from '../../actions/listActions';
import { saveNewNameInOrder } from '../../actions/nameCorrectionAction';
import { formatDateTime } from '../../helpers/format';

const VIEWS = [
  { key: 'pending', label: 'Pending', attention: true },
  { key: 'complete', label: 'Complete' },
  { key: 'all', label: 'All' },
];

export default function NameCorrection() {
  const dispatch = useDispatch();
  const { confirm, toast } = useOverlays();
  const [record, setRecord] = useState(null);
  const [newName, setNewName] = useState('');
  const [explanation, setExplanation] = useState('');

  const list = useListState({
    views: VIEWS,
    fetcher: listNameCorrections,
    countsFetcher: getNameCorrectionCounts,
    initialSort: { column: 'created_at', direction: 'desc' },
  });

  const open = useCallback((row) => {
    setRecord(row);
    setNewName(row.analysis?.new_name || '');
    setExplanation(row.analysis?.explanation || '');
  }, []);

  const submit = useCallback(async () => {
    const ok = await confirm({
      title: 'Submit correction?',
      body: `“${newName}” will be sent to ${record.client_name} and the order marked complete. This cannot be edited afterwards.`,
      confirmLabel: 'Submit correction',
    });
    if (!ok) return;
    try {
      await saveNewNameInOrder(dispatch, record.id, { new_name: newName, explanation });
      toast('Correction submitted', { tone: 'success' });
      setRecord(null);
      list.reload();
      list.reloadCounts();
    } catch {
      toast('Could not submit the correction', { tone: 'error' });
    }
  }, [confirm, dispatch, record, newName, explanation, toast, list]);

  const columns = useMemo(
    () => [
      { key: 'client', header: 'Client', width: 240, sortable: true, render: (r) => r.client_name || '—' },
      { key: 'gender', header: 'Gender', width: 110, muted: true, render: (r) => r.gender || '—' },
      { key: 'birth_time', header: 'Birth date', width: 200, mono: true, render: (r) => r.birth_time || '—' },
      {
        key: 'created_at',
        header: 'Created at',
        width: 180,
        muted: true,
        sortable: true,
        render: (r) => formatDateTime(r.created_at),
      },
    ],
    []
  );

  const isComplete = record?.status === 'complete';

  return (
    <>
      <PageHeader
        section="Operations"
        title="Name correction"
        subtitle="Numerology name corrections waiting to be written."
      />

      <ListScreen
        list={list}
        columns={columns}
        rowKey={(r) => r.id}
        noun="orders"
        selectable={false}
        searchPlaceholder="Client name or email"
        onRowClick={open}
        empty={{ icon: 'bx-edit-alt', title: 'No corrections', description: 'Nothing is waiting.' }}
      />

      <Drawer
        open={!!record}
        onClose={() => setRecord(null)}
        title={record?.client_name || 'Name correction'}
        status={record?.status}
        footer={
          !isComplete ? (
            <Button variant="primary" onClick={submit} disabled={!newName.trim()}>
              Submit correction
            </Button>
          ) : null
        }
      >
        <DrawerSection title="Birth details">
          <DrawerField label="Client">{record?.client_name}</DrawerField>
          <DrawerField label="Gender">{record?.gender}</DrawerField>
          <DrawerField label="Birth date" mono>
            {record?.birth_time}
          </DrawerField>
          <DrawerField label="Ordered">{formatDateTime(record?.created_at)}</DrawerField>
        </DrawerSection>

        <DrawerSection title="Correction">
          {isComplete ? (
            <>
              <DrawerField label="New name">{record?.analysis?.new_name}</DrawerField>
              <DrawerField label="Explanation">{record?.analysis?.explanation}</DrawerField>
            </>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, paddingTop: 8 }}>
              <Field label="Corrected name" required>
                <TextInput value={newName} onChange={(e) => setNewName(e.target.value)} />
              </Field>
              <Field label="Explanation">
                <Textarea rows={5} value={explanation} onChange={(e) => setExplanation(e.target.value)} />
              </Field>
            </div>
          )}
        </DrawerSection>
      </Drawer>
    </>
  );
}
